import React, { ReactElement, useEffect, useState } from "react";
import TickCircledIcon from "views/components/icons/TickCircledIcon";
import CrossCircledIcon from "views/components/icons/CrossCircledIcon";
import ImageJaggedMountainIconRaw from "views/components/icons/ImageJaggedMountainIcon";
import TooltipUntyped from "views/components/Tooltip";
import style from "./style.module.css";
import { DndUploadEntry } from "state/ui/dndUploadProgress/dndUploadProgressTypes";
import { DndUploadAggregate } from "state/ui/dndUploadProgress/dndUploadProgressSelectors";
import { computeEtaSeconds, formatEta } from "./uploadEta";

const ERROR_TOOLTIP_ID = "dnd-upload-error-tip";

// PropTypes-derived type infers all props as required; we only set a few.
const Tooltip = TooltipUntyped as unknown as (props: {
  id: string;
  place?: string;
  multiline?: boolean;
}) => ReactElement;

const ImageJaggedMountainIcon =
  ImageJaggedMountainIconRaw as unknown as (props: {
    isTabVersion?: boolean;
    size?: string;
    color?: string;
  }) => ReactElement;

export interface DndUploadProgressToastProps {
  uploads: DndUploadEntry[];
  aggregate: DndUploadAggregate;
  onDismissAll: () => void;
  positionStyle?: React.CSSProperties;
}

const buildHeaderLabel = (aggregate: DndUploadAggregate): string => {
  if (aggregate.uploadingFiles > 0) {
    if (aggregate.totalFiles === 1) return "Uploading 1 file";
    return `Uploading ${aggregate.uploadingFiles} of ${aggregate.totalFiles} files`;
  }
  if (aggregate.hasError && aggregate.erroredFiles === aggregate.totalFiles) {
    return aggregate.totalFiles === 1 ? "Upload failed" : "Uploads failed";
  }
  if (aggregate.hasError) {
    return `${aggregate.completedFiles} of ${aggregate.totalFiles} uploaded`;
  }
  return aggregate.totalFiles === 1
    ? "Upload complete"
    : `${aggregate.totalFiles} uploads complete`;
};

// Re-renders every `intervalMs` while `isActive` is true. Used to tick ETA
// estimates down between XHR progress events.
const useTick = (intervalMs: number, isActive: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    const id = window.setInterval(() => setTick(t => t + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, isActive]);
};

const UploadRowStatus = ({
  entry,
  now
}: {
  entry: DndUploadEntry;
  now: number;
}) => {
  if (entry.status === "success") {
    return <TickCircledIcon size={16} color="#00bd62" />;
  }
  if (entry.status === "error") {
    const icon = <CrossCircledIcon size={16} color="#e64826" className="" />;
    if (!entry.errorMessage) return icon;
    return (
      <span
        className={style.errorIconWrapper}
        data-tip={entry.errorMessage}
        data-for={ERROR_TOOLTIP_ID}
        data-testid="dnd-upload-error-icon"
      >
        {icon}
      </span>
    );
  }
  const etaSeconds = computeEtaSeconds(
    entry.progress,
    entry.firstProgressAt ?? entry.startedAt,
    now
  );
  return (
    <div className={style.uploadProgressContainer}>
      {etaSeconds != null && (
        <span className={style.uploadEta} data-testid="dnd-upload-eta">
          {formatEta(etaSeconds)}
        </span>
      )}
      <div className={style.uploadProgressBar}>
        <div
          className={style.uploadProgressBarFill}
          style={{ width: `${entry.progress}%` }}
        />
      </div>
    </div>
  );
};

const DndUploadProgressToast = ({
  uploads,
  aggregate,
  onDismissAll,
  positionStyle
}: DndUploadProgressToastProps) => {
  const hasActiveUpload = aggregate.uploadingFiles > 0;
  useTick(1000, hasActiveUpload);

  if (uploads.length === 0) return null;

  const headerLabel = buildHeaderLabel(aggregate);
  const canDismiss = aggregate.uploadingFiles === 0;
  const now = Date.now();

  return (
    <div
      className={style.toast}
      style={positionStyle}
      role="status"
      aria-live="polite"
      data-testid="dnd-upload-progress-toast"
    >
      <div className={style.header}>
        <span className={style.headerLabel}>{headerLabel}</span>
        {canDismiss && (
          <button
            type="button"
            className={style.dismissButton}
            onClick={onDismissAll}
            aria-label="Dismiss upload notification"
            data-testid="dnd-upload-progress-toast-dismiss"
          >
            ×
          </button>
        )}
      </div>
      <div className={style.rowList}>
        {uploads.map(entry => (
          <div
            key={entry.uploadId}
            className={style.uploadTableRow}
            data-upload-id={entry.uploadId}
            data-status={entry.status}
          >
            <div className={style.uploadImageIconWrapper}>
              <ImageJaggedMountainIcon
                isTabVersion={false}
                size="20px"
                color="#9a9b9d"
              />
            </div>
            <div className={style.uploadRowText}>{entry.fileName}</div>
            <div className={style.uploadRowRight}>
              <UploadRowStatus entry={entry} now={now} />
            </div>
          </div>
        ))}
      </div>
      <Tooltip id={ERROR_TOOLTIP_ID} place="left" multiline />
    </div>
  );
};

export default DndUploadProgressToast;
